// components/FloatingYouTube.tsx
"use client";

import { useState, useRef, useEffect } from 'react';
import YouTube, { YouTubeEvent, YouTubePlayer } from 'react-youtube';
import { FaTimes, FaExpand, FaCompress } from 'react-icons/fa';

interface FloatingYouTubeProps {
  videoId: string;
}

export default function FloatingYouTube({ videoId }: FloatingYouTubeProps) {
  const [isVisible, setIsVisible] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 180, height: 101 });
  const playerRef = useRef<YouTubePlayer | null>(null);

  // Drag tracking refs
  const dragStart = useRef({ x: 0, y: 0, offsetX: 0, offsetY: 0 });
  const isDraggingRef = useRef(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // --- Update dimensions on client and on resize ---
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const updateDimensions = () => {
      const isMobile = window.innerWidth < 768;
      const baseWidth = isExpanded ? (isMobile ? 280 : 360) : (isMobile ? 120 : 180);
      const baseHeight = Math.round(baseWidth * (9 / 16));
      setDimensions({ width: baseWidth, height: baseHeight });
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [isExpanded]);

  const closeVideo = () => setIsVisible(false);

  const toggleExpand = () => {
    if (!isDraggingRef.current) {
      setIsExpanded(!isExpanded);
      if (!isExpanded && playerRef.current) {
        playerRef.current.unMute();
      }
    }
  };

  const onReady = (event: YouTubeEvent) => {
    playerRef.current = event.target;
    event.target.mute();
    event.target.playVideo();
  };

  // --- Drag Logic ---
  const handleDragStart = (clientX: number, clientY: number) => {
    isDraggingRef.current = false;
    dragStart.current = {
      x: clientX,
      y: clientY,
      offsetX: dragOffset.x,
      offsetY: dragOffset.y,
    };
  };

  const handleDragMove = (clientX: number, clientY: number) => {
    const deltaX = clientX - dragStart.current.x;
    const deltaY = clientY - dragStart.current.y;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    if (distance > 5) {
      isDraggingRef.current = true;
      setIsDragging(true);
    }

    if (isDraggingRef.current || distance > 5) {
      setDragOffset({
        x: dragStart.current.offsetX + deltaX,
        y: dragStart.current.offsetY + deltaY,
      });
    }
  };

  const handleDragEnd = () => {
    setIsDragging(false);
    setTimeout(() => {
      isDraggingRef.current = false;
    }, 10);
  };

  // --- Mouse Events (capture phase on container) ---
  const onMouseDownCapture = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('button')) return;
    handleDragStart(e.clientX, e.clientY);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  const onMouseMove = (e: MouseEvent) => {
    handleDragMove(e.clientX, e.clientY);
  };

  const onMouseUp = () => {
    handleDragEnd();
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  };

  // --- Touch Events (capture phase on container) ---
  const onTouchStartCapture = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    if ((e.target as HTMLElement).closest('button')) return;
    handleDragStart(touch.clientX, touch.clientY);
    document.addEventListener('touchmove', onTouchMove);
    document.addEventListener('touchend', onTouchEnd);
  };

  const onTouchMove = (e: TouchEvent) => {
    const touch = e.touches[0];
    handleDragMove(touch.clientX, touch.clientY);
  };

  const onTouchEnd = () => {
    handleDragEnd();
    document.removeEventListener('touchmove', onTouchMove);
    document.removeEventListener('touchend', onTouchEnd);
  };

  // Cleanup
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  if (!isVisible) return null;

  const { width, height } = dimensions;

  const opts = {
    height: height.toString(),
    width: width.toString(),
    playerVars: {
      autoplay: 1,
      mute: 1,
      loop: 1,
      playsinline: 1,
      rel: 0,
      modestbranding: 1,
    },
  };

  return (
    <div
      ref={containerRef}
      className="fixed z-50 select-none touch-none"
      style={{
        left: '24px',
        bottom: '24px',
        transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
        width: width,
        transition: isDragging ? 'none' : 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
        cursor: isDragging ? 'grabbing' : 'grab',
        touchAction: 'none',
      }}
      onMouseDownCapture={onMouseDownCapture}
      onTouchStartCapture={onTouchStartCapture}
    >
      <div
        className="relative rounded-md overflow-hidden bg-[#292929] shadow-lg"
        style={{ aspectRatio: '9/16' }}
      >
        {/* Close Button */}
        <button
          onClick={(e) => { e.stopPropagation(); closeVideo(); }}
          className="absolute top-1 right-1 z-40 w-6 h-6 bg-white rounded-full flex items-center justify-center transition-all hover:bg-[#f62b1e] hover:scale-110 shadow-lg border border-gray-200 group"
          aria-label="Close video"
        >
          <FaTimes className="text-[#292929] text-xs group-hover:text-white transition-colors" />
        </button>

        {/* Expand / Collapse Button */}
        <button
          onClick={(e) => { e.stopPropagation(); toggleExpand(); }}
          className="absolute top-1 right-9 z-40 w-6 h-6 bg-white rounded-full flex items-center justify-center transition-all hover:bg-[#f62b1e] hover:scale-110 shadow-lg border border-gray-200 group"
          aria-label={isExpanded ? 'Collapse' : 'Expand'}
        >
          {isExpanded ? (
            <FaCompress className="text-[#292929] text-xs group-hover:text-white transition-colors" />
          ) : (
            <FaExpand className="text-[#292929] text-xs group-hover:text-white transition-colors" />
          )}
        </button>

        {/* YouTube Player */}
        <div
          className="relative w-full h-full"
          onClick={!isExpanded ? toggleExpand : undefined}
        >
          <YouTube
            videoId={videoId}
            opts={opts}
            onReady={onReady}
            className="w-full h-full"
            iframeClassName={`w-full h-full ${!isExpanded ? 'pointer-events-none' : ''}`}
          />
        </div>

        {/* Bottom gradient (expanded) */}
        {isExpanded && (
          <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-black/50 to-transparent pointer-events-none" />
        )}
      </div>
    </div>
  );
}